import { useEffect, useMemo, useReducer, useState } from 'react'
import { useApolloClient } from '@apollo/client'
import { useHistory, useLocation } from 'react-router-dom'

import { ListTerrotoriesSortOrder, TimeUnit } from 'generated/graphql'
import type { ListTerrotoriesSortFields } from 'generated/graphql'
import { useTerritoryLazyQueries, useTerritoryQueries } from 'hooks-queries/territory'
import { useTranslate } from 'lib/tolgee'
import { PanelStore } from 'stores'
import analyticsTracker from 'utils/analyticsTracker'
import { getNormalizedTerritoriesDataForTerritoriesTable } from 'utils/getNormalizedTerritoriesDataForTerritoriesTable'
import { getTerritoryUrlByTerritoryId } from 'utils/getTerritoryUrlByTerritoryId'

import {
  GET_COUNTRY_THEMES_LOG,
  SEARCH_TERRITORY_OPTIONS_LOG,
  SEARCH_TERRITORY_TABLE_LOG,
  SEARCH_TERRITORY_TABLE_TAKE,
  SEARCH_TERRITORY_TABLE_TYPES,
  SEARCH_URL_PARAM,
} from './theme-list-tab.consts'
import { translations } from './theme-list-tab.i18n'
import type { ISearchOption, TThemeListTab } from './theme-list-tab.types'
import {
  getSearchOptions,
  loadSearchResultsMetrics,
  searchResultsMetricsReducer,
  sortSearchResults,
  withSearchResultLevel,
  withSearchResultMetrics,
} from './theme-list-tab.utils'
import { DEFAULT_SORT, DEFAULT_TIME, GLOBAL_TAXONOMY_THEMES_LIMIT } from '../common/global-topic.consts'
import type { TTableOrder } from '../common/global-topic.types'
import { getNextTableOrder, handleSort } from '../common/global-topic.utils'

export const useThemeListTabModel = (): TThemeListTab => {
  const [themesOrder, setThemesOrder] = useState<TTableOrder>(DEFAULT_SORT)
  const [searchOrder, setSearchOrder] = useState<{ search: string; order: TTableOrder } | null>(null)
  const [metricsRetryCount, setMetricsRetryCount] = useState(0)
  const [metricsState, dispatchMetrics] = useReducer(searchResultsMetricsReducer, undefined)
  const client = useApolloClient()
  const t = useTranslate()
  const location = useLocation()
  const history = useHistory()
  const workspaceId = Object(PanelStore?.selectedPanel)?.id

  const searchParams = useMemo(() => new URLSearchParams(location.search), [location.search])
  const search = searchParams.get(SEARCH_URL_PARAM)?.trim() ?? ''
  const isSearching = Boolean(search)

  const timeRange = useMemo(
    () => ({
      timeUnit: TimeUnit.Month,
      timeValue: Number(searchParams.get('dateRange')) || DEFAULT_TIME,
    }),
    [searchParams]
  )

  const resultsOrder = searchOrder?.search === search ? searchOrder.order : null
  const order = isSearching ? resultsOrder : themesOrder

  const themesQuery = useTerritoryQueries({
    queryName: 'getCountryThemes',
    queryVariables: {
      pagination: { take: GLOBAL_TAXONOMY_THEMES_LIMIT },
      timeRange,
    },
    errorLog: GET_COUNTRY_THEMES_LOG,
    options: { skip: isSearching },
  })

  const searchTerritoryTableQuery = useTerritoryQueries({
    queryName: 'searchTerritoryTable',
    queryVariables: {
      text: search,
      types: SEARCH_TERRITORY_TABLE_TYPES,
      take: SEARCH_TERRITORY_TABLE_TAKE,
    },
    errorLog: SEARCH_TERRITORY_TABLE_LOG,
    options: { skip: !isSearching },
  })

  const activeQuery = isSearching ? searchTerritoryTableQuery : themesQuery
  const territories = isSearching
    ? searchTerritoryTableQuery.data?.searchTerritory?.data
    : themesQuery.data?.listCountryTerritories?.data
  const totalCount = isSearching
    ? searchTerritoryTableQuery.data?.searchTerritory?.totalCount
    : themesQuery.data?.listCountryTerritories?.totalCount

  const searchResults = searchTerritoryTableQuery.data?.searchTerritory?.data
  const searchResultIds = useMemo(() => searchResults?.map(result => result.territoryId) ?? [], [searchResults])
  const metricsKey = `${searchResultIds.join(',')}:${timeRange.timeValue}:${metricsRetryCount}`

  useEffect(
    function loadSearchResultsMetricsInBackground() {
      if (!searchResultIds.length) return
      let isCancelled = false

      loadSearchResultsMetrics({
        client,
        territoryIds: searchResultIds,
        variables: { timeRange },
        onResultSettled: (territoryId, metrics) => dispatchMetrics({ key: metricsKey, territoryId, metrics }),
        isCancelled: () => isCancelled,
      })

      return () => {
        isCancelled = true
      }
    },
    [client, metricsKey, searchResultIds, timeRange]
  )

  const currentMetrics = metricsState?.key === metricsKey ? metricsState : undefined
  const metricsById = useMemo(() => currentMetrics?.metricsById ?? {}, [currentMetrics])
  const failedMetricsIds = useMemo(() => currentMetrics?.failedIds ?? [], [currentMetrics])
  const areMetricsSettled = Object.keys(metricsById).length + failedMetricsIds.length >= searchResultIds.length

  const data = useMemo(() => {
    const normalizedTerritories = getNormalizedTerritoriesDataForTerritoriesTable(
      territories,
      timeRange.timeValue,
      workspaceId,
      undefined,
      undefined
    )

    if (!isSearching) return order ? handleSort(normalizedTerritories, order) : normalizedTerritories

    const rows = withSearchResultMetrics(withSearchResultLevel(normalizedTerritories, t), {
      metricsById,
      failedIds: failedMetricsIds,
    })
    return order && areMetricsSettled ? sortSearchResults(rows, order, failedMetricsIds) : rows
  }, [
    areMetricsSettled,
    failedMetricsIds,
    isSearching,
    metricsById,
    order,
    t,
    territories,
    timeRange.timeValue,
    workspaceId,
  ])

  const handleSetOrder = (field: ListTerrotoriesSortFields) => {
    const newOrder = order ? getNextTableOrder(order, field) : { field, order: ListTerrotoriesSortOrder.Desc }

    if (isSearching) {
      setSearchOrder({ search, order: newOrder })
    } else {
      setThemesOrder(newOrder)
    }

    const { SORT } = analyticsTracker.eventNames
    analyticsTracker.eventTrack(SORT, {
      Page: 'Search Topics Page',
      'Sort field': field,
      'Sort order': newOrder.order,
    })
  }

  const [searchTerritoryOptions] = useTerritoryLazyQueries({
    queryName: 'searchTerritoryOptions',
    errorLog: SEARCH_TERRITORY_OPTIONS_LOG,
  })

  const handleTypeSearch = (text: string, callback: (options: ISearchOption[]) => void) => {
    const term = text.trim()
    if (!term) {
      callback([])
      return
    }

    searchTerritoryOptions({
      variables: { text: term, types: SEARCH_TERRITORY_TABLE_TYPES, take: SEARCH_TERRITORY_TABLE_TAKE },
    })
      .then(({ data }) =>
        getSearchOptions(data?.searchTerritory?.data, term, t(translations.seeResults({ search: term })), t)
      )
      .catch(() => [])
      .then(callback)
  }

  const handleSubmitSearch = (text: string) => {
    const value = text.trim()
    const nextSearchParams = new URLSearchParams(location.search)

    if (value) {
      nextSearchParams.set(SEARCH_URL_PARAM, value)

      const { FILTER } = analyticsTracker.eventNames
      analyticsTracker.eventTrack(FILTER, {
        Page: 'Search Topics Page',
        Filter: 'title',
        'Filter label': value,
      })
    } else {
      nextSearchParams.delete(SEARCH_URL_PARAM)
    }

    history.push({ pathname: location.pathname, search: nextSearchParams.toString() })
  }

  const handleSelectSearchOption = (option: ISearchOption | null) => {
    if (!option) return
    if (option.value) {
      history.push(getTerritoryUrlByTerritoryId(option.value, workspaceId))
      return
    }
    handleSubmitSearch(option.searchTerm)
  }

  return {
    data,
    error: Boolean(activeQuery.error),
    isLoading: activeQuery.loading,
    refetch: () => {
      activeQuery.refetch()
      if (isSearching) setMetricsRetryCount(count => count + 1)
    },
    totalCount,
    handleSetOrder,
    order,
    search,
    handleTypeSearch,
    handleSelectSearchOption,
    handleSubmitSearch,
  }
}
